import { useEffect, useRef, useState } from "react";

interface Props {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Ancho inicial en px (desktop). Default: 50% del viewport. */
  defaultWidth?: number;
  /** Mínimo y máximo en px (desktop). */
  minWidth?: number;
  maxWidth?: number;
  /** localStorage key para persistir el ancho. */
  storageKey?: string;
}

const MOBILE_BREAKPOINT = 768;

export function ResizableDrawer({
  open,
  onClose,
  children,
  defaultWidth,
  minWidth = 400,
  maxWidth = 1400,
  storageKey = "siteplan-drawer-width",
}: Props) {
  const [isMobile, setIsMobile] = useState(
    () => window.innerWidth < MOBILE_BREAKPOINT,
  );
  const [width, setWidth] = useState<number>(() => {
    if (typeof window === "undefined") return 600;
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) return Number(saved);
    } catch {
      /* ignore */
    }
    return defaultWidth ?? Math.round(window.innerWidth * 0.5);
  });

  const isDragging = useRef(false);

  // Detectar cambios de viewport para responsive
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile) {
        // Cap el ancho al viewport actual
        setWidth((w) => Math.min(w, window.innerWidth - 100));
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, onClose]);

  // Lógica de drag para resize
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging.current) return;
      const newWidth = window.innerWidth - e.clientX;
      const clamped = Math.max(minWidth, Math.min(maxWidth, newWidth));
      setWidth(clamped);
    };

    const handleMouseUp = () => {
      if (isDragging.current) {
        isDragging.current = false;
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        try {
          localStorage.setItem(storageKey, String(width));
        } catch {
          /* ignore */
        }
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [minWidth, maxWidth, width, storageKey]);

  const startDrag = () => {
    isDragging.current = true;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  };

  if (!open) return null;

  const drawerWidth = isMobile ? "100%" : `${width}px`;

  return (
    <>
      {/* Backdrop solo en mobile */}
      {isMobile && (
        <div
          className="fixed inset-0 z-40 bg-black/30"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className="fixed right-0 top-0 z-50 flex h-full bg-white shadow-2xl"
        style={{
          width: drawerWidth,
          transition: isDragging.current ? "none" : "transform 200ms",
        }}
      >
        {/* Resize handle (solo desktop) */}
        {!isMobile && (
          <div
            onMouseDown={startDrag}
            className="group absolute left-0 top-0 z-10 flex h-full w-1 cursor-col-resize items-center justify-center hover:w-1.5"
            style={{
              backgroundColor: isDragging.current ? "#ADD010" : "#EBEBEB",
              transition: "background-color 120ms, width 120ms",
            }}
          >
            <div
              className="h-12 w-1 rounded-full opacity-0 group-hover:opacity-100"
              style={{ backgroundColor: "#ADD010", transition: "opacity 120ms" }}
            />
          </div>
        )}

        {/* Contenido */}
        <div className="h-full w-full overflow-hidden">{children}</div>
      </div>
    </>
  );
}